import React, { useState } from 'react';
import { Play, CheckCircle, AlertCircle } from 'lucide-react';


interface BarcodeItem {
    id: string;
    barcode: string;
    stockCardId: string;
    epc: string | null;
    status: 'waiting' | 'matched' | 'error';
}


// Mock barkod listesi - Gerçek uygulamada API'den gelecek
const mockBarcodes: BarcodeItem[] = [
    { id: '1', barcode: '3000000001', stockCardId: '1', epc: null, status: 'waiting' },
    { id: '2', barcode: '3000000002', stockCardId: '1', epc: null, status: 'waiting' },
    { id: '3', barcode: '3000000003', stockCardId: '2', epc: null, status: 'waiting' },
    { id: '4', barcode: '3000000004', stockCardId: '2', epc: null, status: 'waiting' },
];

const BarcodeEpcMatchingPage: React.FC = () => {
    const [isReading, setIsReading] = useState(false);
    const [barcodes, setBarcodes] = useState<BarcodeItem[]>(mockBarcodes);
    const [popupBarcode, setPopupBarcode] = useState<BarcodeItem | null>(null);
    const [popupEpc, setPopupEpc] = useState('');
    const [isCompleting, setIsCompleting] = useState(false);
    const [showPopup, setShowPopup] = useState(false);

    // En az bir eşleştirme var mı kontrolü
    const hasAnyMatches = barcodes.some(barcode => barcode.epc !== null);

    // İşlemi tamamlama fonksiyonu
    const handleComplete = async () => {
        if (!hasAnyMatches) return;

        setIsCompleting(true);
        try {
            // Eşleştirilen barkod-EPC çiftlerini al
            const matches = barcodes
                .filter(barcode => barcode.epc !== null)
                .map(barcode => ({
                    barcode: barcode.barcode,
                    epc: barcode.epc,
                    stockCardId: barcode.stockCardId
                }));

            // TODO: Burada API'ye kayıt işlemi yapılacak
            console.log('Eşleştirmeler kaydediliyor:', matches);

            // Simüle edilmiş API çağrısı
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Başarılı mesajı göster
            alert('Eşleştirmeler başarıyla kaydedildi!');

            // Listeyi sıfırla
            setBarcodes(mockBarcodes);
            setPopupBarcode(null);
        } catch (error) {
            console.error('Kayıt sırasında hata:', error);
            alert('Eşleştirmeler kaydedilirken bir hata oluştu!');
        } finally {
            setIsCompleting(false);
        }
    };

    // Barkod Oku butonu
    const handleStartReading = () => {
        setIsReading(true);
        // Listedeki ilk eşleştirilmemiş barkodu bul
        const nextBarcode = barcodes.find(b => !b.epc);
        setPopupBarcode(nextBarcode || null);
        setPopupEpc('');
        setShowPopup(true);
    };

    return (
        <div className="bg-transparent rounded-xl shadow-none border-0 p-6 max-w-8xl mx-auto space-y-8">
            <div>
                <h1 className="text-3xl font-semibold text-slate-800 mb-8">Barkod - EPC Eşleştirme</h1>
            </div>

            {/* Barkod Listesi ve EPC Eşleştirme */}
            <div className="bg-white rounded-xl border-2 border-slate-200 overflow-hidden">
                <div className="bg-slate-50 px-8 py-6 border-b-2 border-slate-200 flex justify-between items-center">
                    <h3 className="text-3xl font-medium text-slate-800">Barkod Listesi</h3>
                    <div className="flex gap-4">
                        <button
                            onClick={handleStartReading}
                            disabled={isReading}
                            className={`flex items-center justify-center gap-3 px-8 py-4 rounded-xl 
                                    ${isReading
                                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                    : 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800'} 
                                    transition-colors text-xl font-medium`}
                        >
                            <Play className="w-6 h-6" />
                            <span>{isReading ? 'Barkod Okunuyor...' : 'Barkod Oku'}</span>
                        </button>
                        <button
                            onClick={handleComplete}
                            disabled={!hasAnyMatches || isReading || isCompleting}
                            className={`flex items-center justify-center gap-3 px-8 py-4 rounded-xl 
                                    ${!hasAnyMatches || isReading || isCompleting
                                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                    : 'bg-green-600 text-white hover:bg-green-700 active:bg-green-800'} 
                                    transition-colors text-xl font-medium`}
                        >
                            <span>Onayla</span>
                        </button>
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200">
                        <thead className="bg-slate-50">
                            <tr>
                                <th className="px-8 py-6 text-left text-xl font-medium text-slate-700">
                                    Barkod
                                </th>
                                <th className="px-8 py-6 text-left text-xl font-medium text-slate-700">
                                    EPC
                                </th>
                                <th className="px-8 py-6 text-left text-xl font-medium text-slate-700">
                                    Durum
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-slate-100">
                            {barcodes.map((item) => (
                                <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                                    <td className="px-8 py-8 whitespace-nowrap text-2xl font-mono text-slate-900">
                                        {item.barcode}
                                    </td>
                                    <td className="px-8 py-8 whitespace-nowrap text-2xl font-mono text-slate-700">
                                        {item.epc || '-'}
                                    </td>
                                    <td className="px-8 py-8">
                                        {item.status === 'matched' ? (
                                            <div className="flex items-center gap-2 text-green-600">
                                                <CheckCircle className="w-6 h-6" />
                                                <span className="text-xl">Eşleştirildi</span>
                                            </div>
                                        ) : item.status === 'error' ? (
                                            <div className="flex items-center gap-2 text-red-600">
                                                <AlertCircle className="w-6 h-6" />
                                                <span className="text-xl">Hata</span>
                                            </div>
                                        ) : (
                                            <span className="text-xl text-slate-600">Bekliyor</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {barcodes.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="px-8 py-10 text-center text-2xl text-slate-500">
                                        Barkod bulunamadı
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Popup için gerekli kodlar */}
            {showPopup && popupBarcode && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl p-10 flex flex-col items-center gap-8 shadow-xl min-w-[400px]">
                        <h2 className="text-2xl font-bold text-blue-700 mb-2">Barkod: {popupBarcode.barcode}</h2>
                        <div className="w-full flex flex-col gap-4">
                            <label className="text-lg text-slate-700">EPC</label>
                            <input
                                type="text"
                                value={popupEpc}
                                onChange={e => setPopupEpc(e.target.value)}
                                className="w-full border-2 border-slate-300 rounded-xl px-4 py-3 text-xl"
                                placeholder="EPC değeri gir veya okut..."
                            />
                        </div>
                        <button
                            className="w-full px-8 py-4 bg-green-600 text-white rounded-xl text-xl font-semibold hover:bg-green-700"
                            onClick={() => {
                                if (!popupEpc) {
                                    alert('Lütfen bir EPC değeri girin veya okutun');
                                    return;
                                }
                                setBarcodes(prev => prev.map(b => b.id === popupBarcode.id ? { ...b, epc: popupEpc, status: 'matched' } : b));
                                setShowPopup(false);
                                setPopupBarcode(null);
                                setPopupEpc('');
                                setIsReading(false);
                            }}
                            disabled={!popupEpc}
                        >
                            Eşleştir
                        </button>
                        <button
                            className="w-full px-8 py-4 bg-slate-200 text-slate-700 rounded-xl text-xl font-semibold hover:bg-slate-300"
                            onClick={() => {
                                setShowPopup(false);
                                setPopupBarcode(null);
                                setPopupEpc('');
                                setIsReading(false);
                            }}
                        >
                            Vazgeç
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BarcodeEpcMatchingPage;
